import { Schema } from 'effect';

// v2: pageCount is now derived from the actual page geometry (was trusted from
// the caller, which could be a stale/wrong audio.page_count and truncated
// multi-page docs in the reader). Both bumped so existing packs/sources — which
// carry the wrong pageCount — are invalidated and rebuilt from scratch (the
// stored sources can't be reused: they'd fail parseDocPackSource's invariants).
// v3: the pack is now built from the FULL OCR (annotate-don't-remove) — every
// section retained with a reading class + read flag, so skipped classes are
// present as `read: false` segments. Old packs are main-content-only; bumping
// both invalidates them and forces a rebuild from the new full-OCR source.
// v4: doc-pack absorbs the former "doc-pack" schema in place (the two formats are
// collapsed into one). The magic reverts to the canonical DOC_PACK_MAGIC and the
// version is bumped so any stray dev/staging packs from the transitional
// `.lpk`/v3 era are invalidated and rebuilt (production never had any).
// v5 (content only): catalog per-class counts split by the segment's read flag —
// `chars`/`words` now cover read:true segments only; read:false content moves to
// `offChars`/`offWords`. Duration/position estimates then agree with playback,
// which honors `segment.read`, not the class default (a small-font body section
// is emitted as `{class:'body', read:false}` while body defaults to read:true).
// The source format is unchanged (segments already carry `read`), so stored
// sources stay valid and re-encode to the new catalog; the content bump makes
// existing packs stale so the backfill/lazy paths rebuild them.
// v6 (faithfulness epoch): a v6 pack is guaranteed to embed the FULL OCR —
// every section retained with its reading class, non-readable classes present as
// `read: false` (annotate-don't-remove). Older packs (≤ v5) were re-encoded from
// already-filtered main-content-only data, so they carry `read:true` body/heading
// only (offChars=0) even at v5. The bump makes v6 the single DB-filterable signal
// for "faithful": an OCR conversion may only stamp v6 when it built from the
// full-OCR sections (materialize-chapters fails loud otherwise), and the backfill
// cron re-converts anything whose `doc_pack_content_version != 6`. Bumping
// invalidates every sub-v6 pack (reader falls back to legacy chapters until
// re-converted — doc-pack was never enabled on prod, so no prod reader impact).
// Source v5 (faithfulness marker, format unchanged): a v5 source is written ONLY
// by materialize-chapters, which since the v6 gate builds OCR sources from the
// full-OCR sections — so "source v5" MEANS "faithful source". The bump is what
// keeps the lazy re-encode path honest: it may only rebuild packs from a
// current-version source, and every pre-v5 source is main-content-only data the
// re-encode could never make faithful (those docs go through the re-conversion
// cron instead). @see doc-pack-corpus-and-reader-correctness.md §7.7.1
export const DOC_PACK_CONTENT_VERSION = 6;
export const DOC_PACK_SOURCE_VERSION = 5;

export const DOC_PACK_MAGIC = 'LSTNPCK1';
export const DOC_PACK_HEADER_BYTES = 12;

export type DocPackRect = readonly [number, number, number, number];
export type DocPackSize = readonly [number, number];
export type DocPackCharRange = readonly [number, number];
export type DocPackPageCharRange = readonly [
  page: number,
  charStart: number,
  charEnd: number,
];

export type DocPackReadingClass =
  | 'body'
  | 'heading'
  | 'citation'
  | 'cross-reference'
  | 'caption'
  | 'footnote'
  | 'endnote'
  | 'equation'
  | 'table'
  | 'header-footer'
  | 'page-number'
  | 'artifact'
  | 'reference-entry';

export const DOC_PACK_READING_CLASS_DEFAULTS = {
  body: true,
  heading: true,
  citation: false,
  'cross-reference': false,
  caption: false,
  footnote: false,
  endnote: false,
  equation: true,
  table: false,
  'header-footer': false,
  'page-number': false,
  artifact: false,
  'reference-entry': false,
} satisfies Record<DocPackReadingClass, boolean>;

export type DocPackAnchor =
  | {
      readonly type: 'rect';
      readonly page: number;
      readonly rects: readonly DocPackRect[];
    }
  | {
      readonly type: 'selector-offset';
      readonly selector: string;
      readonly startOffset: number;
      readonly endOffset: number;
    };

export type DocPackClassCount = {
  /** Chars/words in this class's `read: true` segments (played by default). */
  readonly chars: number;
  readonly words: number;
  /**
   * Chars/words in this class's `read: false` segments — skipped by playback
   * unless the class is overridden on. Omitted when zero, and absent from
   * pre-v5 catalogs (which folded everything into `chars`/`words`); consumers
   * must treat a missing off bucket as 0.
   */
  readonly offChars?: number | undefined;
  readonly offWords?: number | undefined;
};

export type DocPackCatalogPage = {
  readonly n: number;
  readonly size: DocPackSize;
  readonly offset: number;
  readonly length: number;
  readonly textSource: 'born-digital' | 'ocr' | 'html';
  readonly degraded: boolean;
  readonly label?: string | undefined;
  readonly classes: Partial<Record<DocPackReadingClass, DocPackClassCount>>;
};

export type DocPackOutlineItem = {
  readonly title: string;
  readonly page: number;
  readonly chars?: DocPackCharRange | undefined;
  readonly children?: readonly DocPackOutlineItem[] | undefined;
};

export type DocPackCatalog = {
  readonly v: 1;
  readonly audioId: string;
  readonly contentVersion: number;
  readonly pageCount: number;
  readonly pages: readonly DocPackCatalogPage[];
  readonly outline: readonly DocPackOutlineItem[];
};

export type DocPackPageWord = {
  readonly text: string;
  readonly chars: DocPackCharRange;
  readonly rects: readonly DocPackRect[];
  readonly anchor: DocPackAnchor;
};

export type DocPackGeometry = {
  readonly encoding: 'word-rect-delta-v1';
  readonly precision: number;
  readonly data: string;
};

export type DocPackGeometryRecord = {
  readonly chars: DocPackCharRange;
  readonly rect: DocPackRect;
};

export type DocPackPageSentence = {
  readonly chars: DocPackCharRange;
};

export type DocPackPageBlock = {
  readonly id: string;
  readonly label: string;
  readonly bbox: DocPackRect | null;
  readonly order: number;
  readonly chars: DocPackCharRange | null;
  readonly class: DocPackReadingClass;
  readonly artifact?: boolean | undefined;
};

export type DocPackPageSegment = {
  readonly class: DocPackReadingClass;
  readonly chars: DocPackCharRange;
  readonly read: boolean;
  readonly blockId?: string | undefined;
};

export type DocPackPage = {
  readonly page: number;
  readonly size: DocPackSize;
  readonly textSource: 'born-digital' | 'ocr' | 'html';
  readonly degraded: boolean;
  readonly text: string;
  readonly geometry?: DocPackGeometry | undefined;
  readonly words: readonly DocPackPageWord[];
  readonly sentences: readonly DocPackPageSentence[];
  readonly blocks: readonly DocPackPageBlock[];
  readonly segments: readonly DocPackPageSegment[];
};

export type DocPackOcrCoord = {
  readonly page: number;
  readonly coords: readonly [number, number, number, number];
};

export type DocPackOcrWord = {
  readonly text: string;
  readonly page: number;
  readonly coords?: readonly DocPackOcrCoord[] | undefined;
  /**
   * A class for this word that differs from its paragraph's (an inline citation inside a body
   * paragraph). Each run of such words becomes its own segment within the paragraph's block, read
   * per its class default. Ignored in a skipped (`read: false`) paragraph.
   */
  readonly reading_class?: DocPackReadingClass | undefined;
};

export type DocPackOcrSentence = {
  readonly word_timestamps: readonly DocPackOcrWord[];
  readonly coords?: readonly DocPackOcrCoord[] | undefined;
};

export type DocPackOcrParagraph = {
  readonly sentence_timestamps: readonly DocPackOcrSentence[];
  readonly coords?: readonly DocPackOcrCoord[] | undefined;
  readonly reading_class?: string | undefined;
  /**
   * Explicit read/skip decision for this paragraph. When set it overrides the
   * per-class default (`DOC_PACK_READING_CLASS_DEFAULTS`), so source that already
   * knows what legacy read (e.g. the full-OCR build) can keep a non-default-read
   * class (a caption it kept) as `read: true` without the builder demoting it.
   * Absent ⇒ fall back to the class default (byte-identical to prior behavior).
   */
  readonly read?: boolean | undefined;
};

export type DocPackChapterInput = {
  readonly id: string;
  readonly title: string;
  readonly paragraphs: readonly DocPackOcrParagraph[];
};

export type DocPackBuildInput = {
  readonly audioId: string;
  readonly pageCount: number;
  readonly defaultPageSize: DocPackSize;
  /**
   * Per-page sizes in PDF-point space (same space as word rects), keyed by
   * 1-based page number. When a page is present, its size overrides
   * `defaultPageSize`; pages absent from the map fall back to it. Omit the map
   * entirely (HTML/MD paths, or callers without per-page dims) for byte-identical
   * behavior to applying `defaultPageSize` everywhere.
   */
  readonly pageSizes?: ReadonlyMap<number, DocPackSize> | undefined;
  readonly textSource?: 'born-digital' | 'ocr' | 'html' | undefined;
  readonly chapters: readonly DocPackChapterInput[];
};

export type DocPackSource = {
  readonly v: typeof DOC_PACK_SOURCE_VERSION;
  readonly audioId: string;
  readonly pageCount: number;
  readonly pages: readonly DocPackPage[];
  readonly outline: readonly DocPackOutlineItem[];
};

export class DocPackError extends Schema.TaggedError<DocPackError>()(
  'DocPackError',
  {
    code: Schema.Literals([
      'bad-magic',
      'bad-version',
      'truncated',
      'invalid-catalog',
      'invalid-page',
      'invalid-range',
      'invalid-geometry',
    ]),
    message: Schema.String,
  },
) {}

/**
 * Invalid input to the BUILDER half of the codec: a `DocPackBuildInput` /
 * `DocPackSource` projection that fails validation (parseDocPackSource), OCR
 * geometry that references a page past the document (buildDocPackSource), or a
 * malformed word-geometry stream while ENCODING (geometry.ts). Decoding a stored
 * geometry stream is a READ, so its failures are {@link DocPackError} (code
 * `invalid-geometry`), not this. ONE class on
 * purpose — every one of these shares a single recovery path (the pack cannot be
 * built from this data; a caller either fails the build or skips the pack and
 * defers to the re-conversion cron), so distinct codes would buy nothing.
 * `message` carries the specifics.
 *
 * Kept SEPARATE from {@link DocPackError} (the read/catalog corruption taxonomy)
 * — that one describes a broken STORED pack a reader fetched; this one describes
 * bad input handed to the builder before any pack exists.
 */
export class DocPackBuildInputError extends Schema.TaggedError<DocPackBuildInputError>()(
  'DocPackBuildInputError',
  {
    message: Schema.String,
  },
) {}

/**
 * A pack ENCODE failed for an environmental reason — the deflate/compression
 * platform (CompressionStream / fflate) rejected — as opposed to bad input
 * ({@link DocPackBuildInputError}). Kept distinct because the recovery differs:
 * an input error is permanent (the same data will never encode), whereas this is
 * a transient platform failure a caller may retry or skip. Mirrors the read
 * surface's environmental-vs-corrupt split ({@link DocPackRangeFetchError} vs
 * {@link DocPackCorruptError}).
 */
export class DocPackEncodeError extends Schema.TaggedError<DocPackEncodeError>()(
  'DocPackEncodeError',
  {
    message: Schema.String,
  },
) {}

export type DocPackByteRange = readonly [start: number, endInclusive: number];

/**
 * Transient failure while reading pack bytes: network error, timeout, or a
 * non-success HTTP status. Retryable — the stored pack itself may be fine.
 */
export class DocPackRangeFetchError extends Schema.TaggedError<DocPackRangeFetchError>()(
  'DocPackRangeFetchError',
  {
    message: Schema.String,
    details: Schema.Struct({
      url: Schema.String,
      range: Schema.Tuple([Schema.Int, Schema.Int]),
      status: Schema.optional(Schema.Int),
      cause: Schema.optional(Schema.Unknown),
    }),
  },
) {}

/**
 * The bytes were fetched but do not form a valid doc-pack: bad magic, a pack
 * shorter than its own header/catalog claims, or a page blob that fails to
 * decode. NOT retryable — the stored pack is broken and must be rebuilt.
 */
export class DocPackCorruptError extends Schema.TaggedError<DocPackCorruptError>()(
  'DocPackCorruptError',
  {
    message: Schema.String,
    details: Schema.Struct({
      url: Schema.String,
      page: Schema.optional(Schema.Int),
      cause: Schema.optional(Schema.Unknown),
    }),
  },
) {}

/**
 * A requested page is genuinely not present in the doc-pack (the catalog was
 * read successfully, the page just isn't in it). Callers use this to
 * distinguish a real out-of-range reference — typically rejected with a 400 —
 * from a transient read failure ({@link DocPackRangeFetchError}) they can retry
 * or fail open on. Never produced for read/decode failures.
 */
export class DocPackPageAbsentError extends Schema.TaggedError<DocPackPageAbsentError>()(
  'DocPackPageAbsentError',
  { pageNumber: Schema.Int },
) {
  // Unlike the other three errors, `message` is a prototype getter derived
  // from `pageNumber`, not a schema field — it won't appear in own-property
  // serialization (spread, structuredClone, JSON.stringify).
  override get message(): string {
    return `doc-pack page ${this.pageNumber.toString()} is not present in the pack`;
  }
}
